import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import HelpIcon from '@mui/icons-material/Help';
import { BastionContext } from 'context';
import packageInfo from '../../../../package.json';
import i18n from 'i18n-js';

const AuthLayout = ({ children, title, fullBackground = false }) => {
  const { theme } = useContext(BastionContext);
  // import and use muitheme here
  const muiTheme = useTheme();

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{title}</title>
        <link rel="icon" sizes="16x16" type="image/png" href={theme.page.favicon} />
      </Helmet>
      <Box
        data-cy="auth-layout"
        sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', height: '100%' }}
      >
        {!fullBackground && (
          <Box
            data-cy="auth-card-container"
            sx={(muiTheme) => ({
              alignItems: 'center',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              justifyContent: 'space-between',
              position: 'relative',
              width: '40%',
              [muiTheme.breakpoints.down('sm')]: {
                justifyContent: 'center',
                width: '100%',
              },
            })}
          >
            <Box
              sx={{
                boxSizing: 'border-box',
                maxWidth: '450px',
                minHeight: '300px',
                padding: '25px',
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              {children}
              <Typography data-cy="auth-card-container-footer" color="textSecondary" align="center">
                <Box fontSize={14} mb={5} component="span">
                  © {new Date().getFullYear()} Launch Mobility, Inc. | Version:{' '}
                  {packageInfo.version}
                </Box>
              </Typography>
            </Box>
          </Box>
        )}
        <Box
          data-cy="auth-background-container"
          position="relative"
          sx={(muiTheme) => ({
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'center',
            position: 'relative',
            width: '60%',
            [muiTheme.breakpoints.down('sm')]: {
              display: 'none',
            },
            ...(fullBackground
              ? {
                  width: '100% !important',
                  display: 'flex !important',
                }
              : {}),
          })}
        >
          <Box
            data-cy="auth-background-img"
            component="img"
            alt="background"
            src={theme.background.src}
            sx={{
              height: '100%',
              width: '100%',
              left: 0,
              maxWidth: '100%',
              objectFit: 'cover',
              position: 'absolute',
              top: 0,
            }}
          />
          <Box
            data-cy="auth-background-img-color"
            sx={{
              background: '#0066CB 0% 0% no-repeat padding-box',
              height: '100%',
              left: 0,
              opacity: 0.8,
              position: 'absolute',
              top: 0,
              width: '100%',
            }}
          />
          <Box
            data-cy="auth-support"
            sx={(muiTheme) => ({
              position: 'absolute',
              top: 87,
              right: 140,
              [muiTheme.breakpoints.down('sm')]: {
                top: 10,
                right: 55,
              },
            })}
          >
            <Box
              component="a"
              href="mailto:operations@launchmobility.com"
              sx={(muiTheme) => ({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
                [muiTheme.breakpoints.down('sm')]: {
                  top: 10,
                },
              })}
            >
              <HelpIcon style={{ color: muiTheme.palette.primary.active, fontSize: 20 }} />
              <Typography
                sx={(muiTheme) => ({
                  color: muiTheme.palette.primary.active,
                  textAlign: 'center',
                })}
              >
                <Box fontSize={16} component="span" ml={1} display="block">
                  Support
                </Box>
              </Typography>
            </Box>
          </Box>
          <Box
            data-cy="auth-logo"
            sx={{
              maxWidth: 600,
              position: 'relative',
              textAlign: 'center',
              ...(fullBackground
                ? {
                    marginTop: 80,
                  }
                : {}),
            }}
          >
            <img alt="logo" src={theme.logo.src} />
          </Box>
          {fullBackground && (
            <>
              {children}
              <Box
                data-cy="auth-footer"
                sx={(theme) => ({
                  marginTop: 'auto',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: theme.palette.primary.active,
                  width: '100%',
                  height: 50,
                })}
              >
                <Typography color="textSecondary" align="center">
                  <Box fontSize={14} mb={5} component="span">
                    © {new Date().getFullYear()}{' '}
                    <Typography
                      component="span"
                      display="inline"
                      sx={(muiTheme) => ({
                        color: muiTheme.palette.primary.main,
                      })}
                    >
                      {i18n.t('common.lm_name')}
                    </Typography>
                    {` ${i18n.t('common.all_rights_reserved')}`}
                  </Box>
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

// Define prop types for the component
AuthLayout.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  fullBackground: PropTypes.bool,
};

export default AuthLayout;
